import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { assessmentParam, inAssessment } from "./assessmentFilter";
import { useSelector } from "react-redux";
import CodeMirror from "@uiw/react-codemirror";
import { vscodeDark, vscodeLight } from "@uiw/codemirror-theme-vscode";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { java } from "@codemirror/lang-java";
import { cpp } from "@codemirror/lang-cpp";
import { go } from "@codemirror/lang-go";

import styles from "./Assessment.module.css";
import ProblemPane from "./ProblemPane";
import ResultsDock from "./ResultsDock";
import ExecutionBanner from "./ExecutionBanner";
import {
  getProblems,
  saveSubmission,
  createRun,
  finalizeProblem,
  pollRunStatus,
  cancelRun,
  getSubmissionHistory,
} from "./assessmentApi";

const languageExtensions = {
  python: python(),
  javascript: javascript(),
  java: java(),
  cpp: cpp(),
  go: go(),
};

const boilerPlates = {
  python: `# Write your Python code here\n\ndef solution():\n    pass\n`,
  javascript: `// Write your JavaScript code here\n\nfunction solution() {\n    \n}\n`,
  java: `// Write your Java code here\n\npublic class Solution {\n    public static void main(String[] args) {\n        \n    }\n}\n`,
  cpp: `// Write your C++ code here\n\n#include <iostream>\nusing namespace std;\n\nint main() {\n    \n    return 0;\n}\n`,
  go: `// Write your Go code here\n\npackage main\n\nimport "fmt"\n\nfunc main() {\n    fmt.Println("Hello")\n}\n`,
};

const AssessmentWorkspace = () => {
  const { topicId, problemId } = useParams();
  const assessment = assessmentParam(useLocation().search);
  const navigate = useNavigate();
  const selectedModuleId = useSelector((state) => state.module.selectedModuleId);
  const effectiveTopicId = topicId || selectedModuleId;

  // Problem state
  const [problems, setProblems] = useState([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editor state
  const [code, setCode] = useState("");
  const [language, setLanguage] = useState("python");
  const [theme, setTheme] = useState("dark");
  const [customInput, setCustomInput] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  const [submissionId, setSubmissionId] = useState(null);

  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [currentRun, setCurrentRun] = useState(null);
  const [runResult, setRunResult] = useState(null);

  // Results dock state
  const [activeTab, setActiveTab] = useState("testcases");
  const [submissions, setSubmissions] = useState([]);
  const [runError, setRunError] = useState("");

  // Polling abort controller
  const pollingAbortRef = useRef(null);

  const currentProblem = problems[currentProblemIndex];

  // Cleanup polling on problem change or unmount
  useEffect(() => {
    return () => {
      if (pollingAbortRef.current) {
        pollingAbortRef.current.abort();
        pollingAbortRef.current = null;
      }
    };
  }, [currentProblemIndex]);

  // Fetch problems on mount
  useEffect(() => {
    const fetchProblems = async () => {
      if (!effectiveTopicId) return;

      setLoading(true);
      try {
        // Opened from one assessment: only its questions.
        const data = (await getProblems(effectiveTopicId) || []).filter((p) => inAssessment(p, assessment));
        setProblems(data || []);

        if (data?.length > 0) {
          // If problemId is provided, find and select it
          if (problemId) {
            const index = data.findIndex(p => p.id === parseInt(problemId));
            if (index >= 0) setCurrentProblemIndex(index);
          }

          // Load first problem's code
          const problem = data[0];
          if (problem.submission_data?.submission) {
            setCode(problem.submission_data.submission);
            setLanguage(problem.submission_data.language || "python");
            setSubmissionId(problem.submission_data.id);
          } else {
            setCode(boilerPlates["python"]);
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load problems");
      } finally {
        setLoading(false);
      }
    };

    fetchProblems();
  }, [effectiveTopicId, problemId, assessment]);

  // Load problem data when switching
  useEffect(() => {
    if (!currentProblem) return;

    const submission = currentProblem.submission_data;
    if (submission?.submission) {
      setCode(submission.submission);
      setLanguage(submission.language || "python");
      setSubmissionId(submission.id);
    } else {
      setCode(boilerPlates[language]);
      setSubmissionId(null);
    }
    setIsSaved(true);
    setRunResult(null);
    setCurrentRun(null);
  }, [currentProblemIndex, currentProblem]);

  // Fetch submission history when problem changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!currentProblem || !effectiveTopicId) return;
      try {
        const history = await getSubmissionHistory(effectiveTopicId, currentProblem.id);
        setSubmissions(history.submissions || []);
      } catch (err) {
        console.error("Failed to load submission history:", err);
      }
    };
    fetchHistory();
  }, [currentProblem, effectiveTopicId]);

  const handleCodeChange = useCallback((value) => {
    setCode(value);
    setIsSaved(false);
  }, []);

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    if (!currentProblem?.submission_data || currentProblem.submission_data.language !== newLanguage) {
      setCode(boilerPlates[newLanguage]);
      setIsSaved(false);
    }
  };

  const handleSave = async () => {
    if (!currentProblem || !effectiveTopicId) return;

    try {
      const result = await saveSubmission(effectiveTopicId, currentProblem.id, {
        submission: code,
        language,
      });
      setSubmissionId(result.id);
      setIsSaved(true);

      // Update problems state
      setProblems(prev => prev.map((p, i) =>
        i === currentProblemIndex
          ? { ...p, submission_data: { id: result.id, submission: code, language } }
          : p
      ));
    } catch (err) {
      console.error("Failed to save:", err);
    }
  };

  // After the final solution, only custom-input runs (not graded) are allowed.
  const isFinal = Boolean(currentProblem?.final);
  const [confirmingFinal, setConfirmingFinal] = useState(false);
  const [finalError, setFinalError] = useState("");
  const hasAttempt = submissions.length > 0 || Boolean(currentProblem?.submission_data?.id);

  useEffect(() => {
    setConfirmingFinal(false);
    setFinalError("");
  }, [currentProblemIndex]);

  const handleSubmitFinal = async () => {
    if (!currentProblem || !effectiveTopicId) return;
    setFinalError("");
    try {
      const result = await finalizeProblem(effectiveTopicId, currentProblem.id);
      setConfirmingFinal(false);
      setProblems(prev => prev.map((p, i) =>
        i === currentProblemIndex ? { ...p, final: true, answer_key: result.answer_key } : p
      ));
    } catch (err) {
      setFinalError(err.response?.data?.error || "Couldn't submit. Try again.");
    }
  };

  const handleRun = async () => {
    if (!currentProblem || !effectiveTopicId || isRunning) return;
    if (isFinal && !showCustomInput) return;

    // Abort any existing polling
    if (pollingAbortRef.current) {
      pollingAbortRef.current.abort();
    }
    pollingAbortRef.current = new AbortController();

    setIsRunning(true);
    setRunResult(null);
    setRunError("");
    setActiveTab("testcases");

    try {
      // Save first if needed
      let currentSubmissionId = submissionId;
      if (!isSaved || !submissionId) {
        const saveResult = await saveSubmission(effectiveTopicId, currentProblem.id, {
          submission: code,
          language,
        });
        currentSubmissionId = saveResult.id;
        setSubmissionId(saveResult.id);
        setIsSaved(true);
      }

      // Create run
      const run = await createRun(effectiveTopicId, currentProblem.id, {
        submission_id: currentSubmissionId,
        language,
        is_custom_input: showCustomInput,
        custom_input: showCustomInput ? customInput : "",
      });

      setCurrentRun(run);

      // Poll for completion
      const finalStatus = await pollRunStatus(
        effectiveTopicId,
        run.run_id,
        (status) => {
          setCurrentRun(status);
        },
        2000,
        300000,
        pollingAbortRef.current.signal
      );

      setRunResult(finalStatus);
      setCurrentRun(null);

      // Refresh submission history
      const history = await getSubmissionHistory(effectiveTopicId, currentProblem.id);
      setSubmissions(history.submissions || []);
    } catch (err) {
      // Ignore abort errors (user navigated away)
      if (err.message === "Polling aborted") {
        setCurrentRun(null);
        return;
      }

      console.error("Run failed:", err);

      // Handle 409 Conflict - run already in progress (a final answer is also a 409, without a run)
      if (err.response?.status === 409 && err.response.data?.run_id) {
        const existingRun = err.response.data;
        setCurrentRun(existingRun);

        // Resume polling for the existing run
        try {
          const finalStatus = await pollRunStatus(
            effectiveTopicId,
            existingRun.run_id,
            (status) => {
              setCurrentRun(status);
            },
            2000,
            300000,
            pollingAbortRef.current?.signal
          );
          setRunResult(finalStatus);
          setCurrentRun(null);

          // Refresh submission history
          const history = await getSubmissionHistory(effectiveTopicId, currentProblem.id);
          setSubmissions(history.submissions || []);
        } catch (pollErr) {
          // Ignore abort errors
          if (pollErr.message !== "Polling aborted") {
            console.error("Failed to poll existing run:", pollErr);
          }
          setCurrentRun(null);
        }
      } else {
        setCurrentRun(null);
        setRunError(err.response?.data?.message || err.response?.data?.error || "Your code couldn't be run. Try again.");
      }
    } finally {
      setIsRunning(false);
    }
  };

  const handleCancel = async () => {
    if (!currentRun || !effectiveTopicId) return;

    try {
      await cancelRun(effectiveTopicId, currentRun.run_id);
      setCurrentRun(null);
      setIsRunning(false);
    } catch (err) {
      console.error("Failed to cancel:", err);
    }
  };

  const handleReset = () => {
    setCode(boilerPlates[language]);
    setIsSaved(false);
  };

  const handleRestoreSubmission = (submission) => {
    if (submission.submission) {
      setCode(submission.submission);
      setLanguage(submission.language || "python");
      setSubmissionId(submission.id);
      setIsSaved(true);
    }
  };

  const handlePrevious = () => {
    if (currentProblemIndex > 0) {
      setCurrentProblemIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentProblemIndex < problems.length - 1) {
      setCurrentProblemIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getDifficultyClass = (difficulty) => {
    switch (difficulty) {
      case "E": return styles.difficultyEasy;
      case "M": return styles.difficultyMedium;
      case "H": return styles.difficultyHard;
      default: return "";
    }
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case "E": return "EASY";
      case "M": return "MEDIUM";
      case "H": return "HARD";
      default: return difficulty;
    }
  };

  if (loading) {
    return <div className={styles.workspace}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.workspace}>Error: {error}</div>;
  }

  if (!currentProblem) {
    return <div className={styles.workspace}>No problems found</div>;
  }

  return (
    <div className={styles.workspace}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={handleBack}>
            ←
          </button>
          <span className={styles.problemTitle}>
            {currentProblem.description?.slice(0, 40) || `Problem ${currentProblemIndex + 1}`}
          </span>
          <span className={`${styles.difficultyBadge} ${getDifficultyClass(currentProblem.difficulty)}`}>
            {getDifficultyLabel(currentProblem.difficulty)}
          </span>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.questionNav}>
            <button
              className={styles.questionNavBtn}
              onClick={handlePrevious}
              disabled={currentProblemIndex === 0}
            >
              ←
            </button>
            <span className={styles.questionNavText}>
              Question {currentProblemIndex + 1} of {problems.length}
            </span>
            <button
              className={styles.questionNavBtn}
              onClick={handleNext}
              disabled={currentProblemIndex === problems.length - 1}
            >
              →
            </button>
          </div>
        </div>
      </header>

      {/* Three-Pane Layout */}
      <div className={styles.paneContainer}>
        {/* Problem Pane */}
        <ProblemPane problem={currentProblem} />

        {/* Editor Pane */}
        <div className={styles.editorPane}>
          <div className={styles.editorHeader}>
            <div className={styles.editorControls}>
              <div className={styles.editorSelect}>
                <span className={styles.languageIndicator} />
                <select
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}
                >
                  <option value="python">Python 3</option>
                  <option value="cpp">C++</option>
                  <option value="java">Java</option>
                </select>
              </div>
              <div className={styles.editorSelect}>
                <select
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </div>
            </div>
            <div className={styles.editorInfo}>
              <span className={isSaved ? styles.savedIndicator : ""}>
                {isSaved ? "● Saved" : "○ Unsaved"}
              </span>
            </div>
          </div>

          {/* Execution Banner inside editor pane */}
          {isRunning && currentRun && (
            <ExecutionBanner run={currentRun} onCancel={handleCancel} />
          )}

          <div className={styles.editorContent} style={isRunning ? { flex: "1 1 auto", minHeight: 0 } : undefined}>
            <CodeMirror
              value={code}
              onChange={handleCodeChange}
              theme={theme === "dark" ? vscodeDark : vscodeLight}
              extensions={[languageExtensions[language] || python()]}
              height="100%"
              style={{ height: "100%", fontSize: "14px" }}
            />
          </div>

          <div className={styles.editorFooter}>
            <div className={styles.editorFooterLeft}>
              <label
                className={styles.customInputToggle}
                onClick={() => setShowCustomInput(!showCustomInput)}
              >
                <div className={`${styles.toggle} ${showCustomInput ? styles.toggleActive : ""}`}>
                  <div className={styles.toggleKnob} />
                </div>
                Custom input
              </label>
              <span className={styles.shortcutHint}>⌘↵ to run</span>
            </div>
            <div className={styles.editorButtons}>
              <button className={styles.editorBtn} onClick={handleReset}>
                Reset
              </button>
              {isFinal ? (
                <span style={{ fontSize: 12, color: "#9fd5b4", alignSelf: "center" }} data-testid="final-note">
                  Final solution submitted · see the Solution tab
                </span>
              ) : confirmingFinal ? (
                <>
                  <span style={{ fontSize: 12, color: "#c9d6e3", alignSelf: "center" }}>
                    Submit as final? Your best run counts; no more graded runs, and the solution will be shown.
                  </span>
                  <button className={styles.editorBtn} onClick={() => setConfirmingFinal(false)}>Cancel</button>
                  <button className={`${styles.editorBtn} ${styles.runBtn}`} onClick={handleSubmitFinal}>Yes, submit</button>
                </>
              ) : (
                <>
                  <button className={styles.editorBtn} onClick={handleSave}>
                    Save draft
                  </button>
                  <button className={styles.editorBtn} onClick={() => setConfirmingFinal(true)}
                          disabled={!hasAttempt || isRunning} title={hasAttempt ? "" : "Run your code at least once first"}>
                    Submit final
                  </button>
                </>
              )}
              {(!isFinal || showCustomInput) && !confirmingFinal && (
                <button
                  className={`${styles.editorBtn} ${styles.runBtn}`}
                  onClick={handleRun}
                  disabled={isRunning}
                >
                  {isRunning ? (
                    <>
                      <span className={styles.spinner} style={{ width: 13, height: 13 }} />
                      Running…
                    </>
                  ) : isFinal ? (
                    <>▶ Run with custom input</>
                  ) : (
                    <>▶ Run &amp; submit</>
                  )}
                </button>
              )}
              {(finalError || runError) && (
                <span style={{ color: "#f19c9c", fontSize: 12, alignSelf: "center" }} data-testid="run-error">
                  {finalError || runError}
                </span>
              )}
            </div>
          </div>

          {/* Custom Input Area */}
          {showCustomInput && (
            <div style={{
              borderTop: "1px solid #22303f",
              padding: "12px 18px",
              background: "#141d29"
            }}>
              <textarea
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Enter custom input..."
                style={{
                  width: "100%",
                  height: 80,
                  background: "#1c2837",
                  border: "1px solid #2b3a4c",
                  borderRadius: 6,
                  color: "#c9d6e3",
                  padding: 12,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13,
                  resize: "vertical",
                }}
              />
            </div>
          )}
        </div>

        {/* Results Dock */}
        <ResultsDock
          activeTab={activeTab}
          onTabChange={setActiveTab}
          runResult={runResult}
          submissions={submissions}
          totalCases={currentProblem.testcases_data?.length || 0}
          onRestoreSubmission={handleRestoreSubmission}
          onRunSingleCase={(input) => {
            setCustomInput(input);
            setShowCustomInput(true);
            // Trigger run with custom input
            setTimeout(() => handleRun(), 100);
          }}
        />
      </div>
    </div>
  );
};

export default AssessmentWorkspace;
